import { ChangeEvent, Component } from "react";
import {
    createStyles,
    Grid,
    TextField,
    Theme,
    Typography,
    WithStyles,
    withStyles
} from "@material-ui/core";
import PersonalCapacityNotice, {
    PERSONAL_CAPACITY_NOTICE_ID
} from "./PersonalCapacityNotice";

interface IComponentProps {
    firstName: string;
    lastName: string;
    email: string;
    isEdit: boolean;
    isViewMode: boolean;
    showPersonalCapacityNotice?: boolean;
    allowPasswordReset: boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

type AccountDetailsProps = WithStyles<typeof styles> & IComponentProps;

interface IAccountDetailsState {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    confirmEmail: string;
}

const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;

const DarkerDisabledTextField = withStyles({
    root: {
        "& .MuiFormLabel-root.Mui-disabled": {
            color: "rgba(255,255,255, 0.6)" // (default alpha is 0.38)
        }
    }
})(TextField);

const styles = (theme: Theme) =>
    createStyles({
        form: {
            maxWidth: 1000,
            width: "100%"
        },
        sectionHeaderParagraph: {
            borderBottom: `4px solid ${theme.palette.primary.main}`,
            marginTop: theme.spacing(4)
        },
        input: {
            color: "white"
        },
        floatingLabel: {
            color: "#CCCCCC"
        }
    });

class AccountDetails extends Component<
    AccountDetailsProps,
    IAccountDetailsState
> {
    constructor(props: AccountDetailsProps) {
        super(props);

        this.state = {
            confirmPassword: "",
            confirmEmail: "",
            email: "",
            firstName: "",
            lastName: "",
            password: ""
        };
    }

    public componentDidUpdate(prevProps: AccountDetailsProps) {
        if (prevProps.email !== this.props.email) {
            this.setState({
                email: this.props.email,
                firstName: this.props.firstName,
                lastName: this.props.lastName
            });
        }
    }

    public render() {
        const classes = this.props.classes;
        return (
            <div className={classes.form}>
                <WhiteTextTypography
                    className={classes.sectionHeaderParagraph}
                    component="p"
                    variant="h4"
                >
                    Account Details
                </WhiteTextTypography>
                <Grid
                    container={true}
                    spacing={4}
                    role={
                        this.props.showPersonalCapacityNotice
                            ? "group"
                            : undefined
                    }
                    aria-label={
                        this.props.showPersonalCapacityNotice
                            ? "Your name"
                            : undefined
                    }
                    aria-describedby={
                        this.props.showPersonalCapacityNotice
                            ? PERSONAL_CAPACITY_NOTICE_ID
                            : undefined
                    }
                >
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="First Name"
                            name="firstName"
                            autoFocus={true}
                            disabled={this.props.isViewMode}
                            value={this.state.firstName}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Last Name"
                            name="lastName"
                            disabled={this.props.isViewMode}
                            value={this.state.lastName}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                </Grid>
                {this.props.showPersonalCapacityNotice && (
                    <PersonalCapacityNotice />
                )}
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <DarkerDisabledTextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Email"
                            name="email"
                            type="email"
                            disabled={
                                this.props.isEdit || this.props.isViewMode
                            }
                            value={this.state.email}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        {(!this.props.isViewMode ||
                            this.props.allowPasswordReset) && (
                            <TextField
                                color="primary"
                                margin="normal"
                                fullWidth={true}
                                required={true}
                                label="Password"
                                name="password"
                                type="password"
                                value={this.state.password}
                                helperText="Minimum length of 8 characters. One digit required."
                                onChange={this.onChange}
                                InputProps={{
                                    className: classes.input
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        {!this.props.isEdit && (
                            <TextField
                                margin="normal"
                                fullWidth={true}
                                required={true}
                                label="Confirm Email"
                                name="confirmEmail"
                                type="email"
                                disabled={
                                    this.props.isEdit || this.props.isViewMode
                                }
                                value={this.state.confirmEmail}
                                onChange={this.onChange}
                                InputProps={{
                                    className: classes.input
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            />
                        )}
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        {(!this.props.isViewMode ||
                            this.props.allowPasswordReset) && (
                            <TextField
                                margin="normal"
                                fullWidth={true}
                                required={true}
                                label="Confirm Password"
                                name="confirmPassword"
                                type="password"
                                value={this.state.confirmPassword}
                                onChange={this.onChange}
                                InputProps={{
                                    className: classes.input
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
            </div>
        );
    }

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);
        this.props.onChange(event);
    };
}

export default withStyles(styles)(AccountDetails);
